import { _axios } from '@/plugins/axios'
import repositoryConverter from '@/plugins/converters/repositories'
import i18n from '@/plugins/i18n'

const state = {
  questionnaireFilterId: null,
  /**
   * @typedef {Object} SubSkillAir
   * @property {String} id - ID de la sous-compétence
   * @property {String} name - Nom de la sous-compétence
   * @property {String} type - Type de l'objet
   * @property {String} shortName - Nom court pour affichage sur les graphiques
   * @property {Boolean} selected - Sous-compétence sélectionnée dans la vue tree view
   * @property {Number} questionCount - Nombre de questions associées à la sous-compétence
   */
  /**
   * @typedef {Object} SkillAir
   * @property {String} id - ID de la compétence
   * @property {String} name - Nom de la compétence
   * @property {String} type - Type de l'objet
   * @property {String} shortName - Nom court pour affichage sur les graphiques
   * @property {Number} priority - Ordre de priorité d'affichage pour les feedbacks
   * @property {Boolean} selected - Compétence sélectionnée dans la vue tree view
   * @property {Number} maxQuestionCount - Plus grand nombre d'associations de questions
   * parmis cette compétence et ses sous-compétences
   * @property {Number} questionCount - Nombre de question associées à la compétence
   * @property {SubSkillAir[]} children - Tableau des sous-compétences rattachées à la compétence
   */
  /**
   * @typedef {Object} DomainAir
   * @property {String} id - ID de la compétence
   * @property {String} name - Nom de la compétence
   * @property {String} type - Type de l'objet
   * @property {Number} maxQuestionCount - Plus grand nombre de questions liées à une compétence
   * @property {SkillAir[]} children - Tableau des compétences rattachées au domaine
   */
  /**
   * @typedef {Object} RepositoryAir
   * @property {String} id - ID du référentiel
   * @property {String} name - Nom du référentiel
   * @property {String} type - Type de l'objet
   * @property {Number} maxQuestionCount - Plus grand nombre de questions liées à une compétence
   * @property {DomainAir[]} children - Tableau des domaines rattachés au référentiel
   *
   * @type {RepositoryAir}
   */
  repositorySelected: null,
  /**
   * @type {RepositoryAir[]}
   */
  repositories: []
}

const getters = {
  questionnaireFilterId: state => state.questionnaireFilterId,
  repositories: state => state.repositories,
  repositorySelected: state => state.repositorySelected,
  repositorySelectedId: state => state.repositorySelected.id,
  selectedSkills: state => state.repositorySelected && state.repositorySelected.children
    ? state.repositorySelected.children.reduce(
      (selected, domain) => selected.concat(
        domain.children
          .filter(skill => skill.selected)
          .map(s => ({
            id: s.id,
            name: s.name
          }))
          .concat(domain.children.reduce(
            (subskillSelected, skill) => subskillSelected.concat(
              skill.children.filter(sub => sub.selected)
                .map(s => ({
                  id: s.id,
                  name: s.name
                }))
            ), []
          ))
      ), []
    )
    : []
}

const actions = {
  /**
   * Charge l'ensemble des référentiels.
   * Le référentiel sauvegardé dans state.repositorySelected donne l'ID du référentiel à charger.
   *
   * @param commit
   * @param state
   * @param dispatch
   * @return {Promise<void>}
   */
  loadAllRepositories: ({ commit, state, dispatch }) => {
    return _axios.get('repositories/')
      .then(response => {
        commit('LOAD', {
          isUnique: false,
          payload: response.data
        })
        if (state.repositories.length === 1) {
          dispatch('selectRepository', state.repositories[0].id)
        }
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  /**
   * Charge les détails du référentiel selectionné dans le store
   * et les pondérations associées aux compétences si un questionnaire est spécifié
   *
   * @param commit
   * @param dispatch
   * @param state
   * @param {Number} questionnaireId - identifiant du questionnaire dont on souhaite
   * voir les associations questions - compétences
   * @returns {Promise<void>}
   */
  loadRepository: ({ commit, dispatch, state }, questionnaireId = 0) => {
    const url = `repositories/${state.repositorySelected.id}/` +
      (questionnaireId ? `?questionnaire=${questionnaireId}` : '')
    return _axios.get(url)
      .then(response => {
        commit('LOAD', {
          isUnique: true,
          payload: response.data
        })
        if (questionnaireId) commit('UPDATE_WEIGHT')
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  /**
   * @typedef {Object} QuestionSkillAir
   * @property {Number} id - ID de la question associée
   * @property {Number} weight - Pondération de l'association avec la compétence
   * @property {String} label - Label de la question associée
   */
  /**
   * Sauvegarde une compétence.
   * Utilisée dans la modale pour sauvegarder les questions qu'on a ajoutées à la compétence.
   *
   * @param commit
   * @param dispatch
   * @param id {String|Number} ID de la compétence à sauvegarder.
   * @param newQuestions {QuestionSkillAir[]} Tableau contenant les questions associées.
   * @return {Promise<void>}
   */
  saveSkill: ({ commit, dispatch }, { id, newQuestions }) => {
    return _axios.patch(
      `skills/${id}/set_questions/`,
      newQuestions
    )
      .then(response => {
        commit('UPDATE_SKILL_QUESTIONS', {
          id,
          questions: response.data
        })
        commit('UPDATE_WEIGHT')
        dispatch('displayInfo', i18n.t('store.manager.repository.saveSkill.done'), { root: true })
      })
      .catch(error => {
        dispatch(
          'displayError',
          { action: i18n.t('store.manager.repository.saveSkill.failed'), error },
          { root: true }
        )
      })
  },
  selectQuestionnaire: ({ commit }, id) => {
    commit('SELECT_QUESTIONNAIRE', id)
  },
  skillDeselection: ({ commit }, skillId) => {
    commit('SKILL_DESELECT', skillId)
  },
  skillsSelection: ({ commit }, skills) => {
    commit('SKILLS_SELECTION_RESET')
    commit('SKILLS_SELECT', skills)
  },
  skillsSelectionReset: ({ commit }) => {
    commit('SKILLS_SELECTION_RESET')
  },
  /**
   * Permet de sélectionner un référentiel et de le sauvegarder dans le state.
   * Sauvegarde de l'ID du référentiel dans le stockage de la session pour le garder en cas de
   * rafraîchissement de la page.
   *
   * @param commit
   * @param state
   * @param dispatch
   * @param id {String|Number} ID du référentiel sélectionné
   * @return {Promise<void>}
   */
  selectRepository: async ({ commit, state, dispatch }, id) => {
    if (state.repositories.length === 0) {
      await dispatch('loadAllRepositories')
    }
    if (id !== null) {
      commit('SELECT', id)
      sessionStorage.setItem('repository__id', id)
      await dispatch('loadRepository')
    }
  }
}

export const getMaxQuestionCount = (repository) => {
  return repository.children.reduce(
    (maxRepoCount, domain) => Math.max(
      maxRepoCount,
      domain.children.reduce(
        (maxDomainCount, skill) => Math.max(
          maxDomainCount,
          skill.children.reduce(
            (maxCount, subSkill) => Math.max(subSkill.questionCount, maxCount),
            skill.questionCount
          )
        ),
        0
      )
    ),
    0
  )
}

const mutations = {
  LOAD: (state, { isUnique, payload }) => {
    if (isUnique) {
      state.repositorySelected = repositoryConverter.DJToJS.convertRepository(payload)
    } else {
      state.repositories = payload
    }
  },
  SELECT: (state, id) => {
    state.repositorySelected = state.repositories.find(repository => repository.id === id)
  },
  SELECT_QUESTIONNAIRE: (state, id) => {
    state.questionnaireFilterId = id
  },
  SKILL_DESELECT: (state, skillId) => {
    const selectedSkill = (() => {
      for (const domain of state.repositorySelected.children) {
        for (const skill of domain.children) {
          if (skill.id === skillId) {
            return skill
          } else {
            const subSkill = skill.children.find(
              s => s.id === skillId
            )
            if (subSkill) return subSkill
          }
        }
      }
    })()
    selectedSkill.selected = false
  },
  SKILLS_SELECT: (state, skills) => {
    const selectedIds = skills.map(s => s.id)
    state.repositorySelected.children.forEach(domain => {
      domain.children.forEach(skill => {
        if (selectedIds.includes(skill.id)) skill.selected = true

        skill.children.forEach(subSkill => {
          if (selectedIds.includes(subSkill.id)) subSkill.selected = true
        })
      })
    })
  },
  SKILLS_SELECTION_RESET: (state) => {
    state.repositorySelected.children.forEach(domain => {
      domain.children.forEach(skill => {
        skill.selected = false
        skill.children.forEach(subSkill => {
          subSkill.selected = false
        })
      })
    })
  },
  UPDATE_SKILL_QUESTIONS: (state, { questions, id }) => {
    state.repositorySelected.children.some(domain => {
      return domain.children.some(skill => {
        if (skill.id === id) {
          skill.questions = questions
          skill.questionCount = questions.length
          return true
        }
        if (skill.children.length > 0) {
          return skill.children.some(subSkill => {
            if (subSkill.id === id) {
              subSkill.questions = questions
              subSkill.questionCount = questions.length
              return true
            }

            return false
          })
        }
        return false
      })
    })
    state.repositorySelected = {
      ...state.repositorySelected,
      maxQuestionCount: getMaxQuestionCount(state.repositorySelected)
    }
  },
  UPDATE_WEIGHT: (state) => {
    state.repositorySelected = {
      ...state.repositorySelected,
      children: state.repositorySelected.children.map(
        domain => {
          let domainTotalWeight = 0
          return {
            ...domain,
            children: domain.children.map(
              skill => {
                const subSkills = skill.children.map(sub => ({
                  ...sub,
                  totalWeight: sub.questions.reduce((total, { weight }) => total + weight, 0)
                }))
                const totalWeight = subSkills.reduce(
                  (total, { totalWeight }) => total + totalWeight, 0
                ) + skill.questions.reduce((total, { weight }) => total + weight, 0)
                domainTotalWeight += totalWeight
                return {
                  ...skill,
                  children: subSkills,
                  totalWeight
                }
              }
            ),
            totalWeight: domainTotalWeight
          }
        }
      )
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
